import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { MessageCircle, Search, Download } from "lucide-react";
import { toast } from "sonner";
import { formatIDR } from "@/lib/format";
import { ORDER_STATUSES, statusColor } from "./adminConstants";
import { downloadCSV, matchesSearch } from "@/lib/csvExport";
import OrderManage from "@/components/OrderManage";

export default function AdminOrdersTab({ orders, setOrders }) {
  const [manageOrder, setManageOrder] = useState(null);
  const [orderSearch, setOrderSearch] = useState("");

  const filteredOrders = orders.filter((o) =>
    matchesSearch([o.id, o.user_email, o.shipping?.name, o.shipping?.phone], orderSearch)
  );

  const updateOrderStatus = async (orderId, status) => {
    try {
      const { data } = await api.patch(`/admin/orders/${orderId}`, { status });
      setOrders((prev) => prev.map((o) => o.id === orderId ? data : o));
      toast.success(`Order ${orderId.slice(-6)} → ${status}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const exportOrdersCSV = () => {
    if (filteredOrders.length === 0) {
      toast.error("No orders to export");
      return;
    }
    const headers = ["Order ID", "Date", "Customer Name", "Email", "Phone", "Items", "Total (IDR)", "Status", "Payment Method", "Shipping Address"];
    const rows = filteredOrders.map((o) => {
      const ship = o.shipping || {};
      const itemsStr = (o.items || []).map((i) => `${i.name} x${i.quantity}`).join("; ");
      const address = [ship.address_detail, ship.village, ship.district, ship.regency, ship.province, ship.postal_code].filter(Boolean).join(", ");
      return [
        o.id,
        (o.created_at || "").slice(0, 10), // fixed YYYY-MM-DD, unambiguous for bookkeeping/tax
        ship.name || "",
        o.user_email || "",
        ship.phone || "",
        itemsStr,
        o.total,
        o.status,
        o.payment_method || "",
        address,
      ];
    });
    downloadCSV(headers, rows, "mobile-xpressed-orders");
    toast.success(`Exported ${filteredOrders.length} order(s)`);
  };

  return (
    <>
      <div className="mb-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="relative max-w-sm flex-1 min-w-[240px]">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <Input
            data-testid="order-search-input"
            value={orderSearch}
            onChange={(e) => setOrderSearch(e.target.value)}
            placeholder="Search by name, phone, or order ID…"
            className="rounded-full bg-white border-zinc-200 h-11 pl-10"
          />
        </div>
        <Button
          data-testid="export-orders-csv-btn"
          onClick={exportOrdersCSV}
          variant="outline"
          className="rounded-full border-2 border-zinc-200 hover:border-zinc-900 bg-white text-zinc-900 h-11 px-5"
        >
          <Download className="w-4 h-4 mr-1.5" /> Export CSV {orderSearch.trim() && `(${filteredOrders.length})`}
        </Button>
      </div>
      <div className="bg-white rounded-3xl border border-zinc-100 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Order ID</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Items</TableHead>
              <TableHead>Total</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Date</TableHead>
              <TableHead className="w-24 text-right">WA</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orders.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center text-zinc-500 py-10">No orders yet</TableCell></TableRow>
            )}
            {orders.length > 0 && filteredOrders.length === 0 && (
              <TableRow><TableCell data-testid="order-search-empty" colSpan={7} className="text-center text-zinc-500 py-10">No orders match "{orderSearch}"</TableCell></TableRow>
            )}
            {filteredOrders.map((o) => (
              <TableRow key={o.id} data-testid={`admin-order-${o.id}`}>
                <TableCell className="font-mono text-xs">{o.id}</TableCell>
                <TableCell className="text-sm">
                  <div className="font-medium text-zinc-900">{o.shipping?.name || "—"}</div>
                  <div className="text-xs text-zinc-500">{o.user_email}</div>
                  {o.shipping?.phone && <div className="text-xs text-zinc-500">{o.shipping.phone}</div>}
                </TableCell>
                <TableCell className="text-sm">{o.items.reduce((s, i) => s + i.quantity, 0)}</TableCell>
                <TableCell className="font-semibold">{formatIDR(o.total)}</TableCell>
                <TableCell>
                  <Select value={o.status} onValueChange={(v) => updateOrderStatus(o.id, v)}>
                    <SelectTrigger data-testid={`order-status-select-${o.id}`} className={`h-8 rounded-full px-3 text-[10px] font-bold uppercase tracking-[0.15em] border-0 ${statusColor(o.status)}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {ORDER_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell className="text-sm text-zinc-500">{new Date(o.created_at).toLocaleDateString()}</TableCell>
                <TableCell className="text-right">
                  <Button
                    data-testid={`order-manage-btn-${o.id}`}
                    size="sm"
                    variant="outline"
                    onClick={() => setManageOrder(o)}
                    className="rounded-full h-8 px-3 text-xs"
                  >
                    <MessageCircle className="w-3.5 h-3.5 mr-1" />
                    Manage
                    {(o.wa_notifications || []).length > 0 && (
                      <span className="ml-1.5 text-[10px] font-bold bg-emerald-100 text-emerald-700 rounded-full px-1.5 py-0.5">
                        {(o.wa_notifications || []).length}
                      </span>
                    )}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <OrderManage
        order={manageOrder}
        open={!!manageOrder}
        onOpenChange={(v) => { if (!v) setManageOrder(null); }}
        onUpdated={(u) => {
          setOrders((prev) => prev.map((o) => o.id === u.id ? u : o));
          setManageOrder(u);
        }}
      />
    </>
  );
}
